import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, Zap, CheckCircle, Calendar, Hammer, Target } from 'lucide-react';
import Header from '../Home/Header';
import Footer from '../Home/Footer';
import { releaseNotesData, Release, ReleaseSection, ReleaseFeature, ReleaseImpact, ReleaseNotesData } from './ReleaseData';

// Component prop interfaces
interface ReleaseHeaderProps {
    release: Release;
}

interface FeatureSectionProps {
    section: ReleaseSection;
    isExpanded: boolean;
    onToggle: () => void;
}

interface ImpactSummaryProps {
    impact: ReleaseImpact;
}

interface ReleaseNavigationProps {
    releases: ReleaseNotesData;
    currentVersion: string;
    onVersionChange: (version: string) => void;
}

interface ReleaseNotesProps {
    version?: string;
}

// Helper function to get the latest release version
const getLatestReleaseVersion = (releases: ReleaseNotesData): string => {
    const versions = Object.keys(releases);
    if (versions.length === 0) return "";
    
    // Sort versions by semantic versioning (descending)
    const sortedVersions = versions.sort((a, b) => {
        const parseVersion = (version: string) => {
            return version.split('.').map(num => parseInt(num, 10));
        };
        
        const versionA = parseVersion(a);
        const versionB = parseVersion(b);
        
        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const numA = versionA[i] || 0;
            const numB = versionB[i] || 0;
            
            if (numA !== numB) {
                return numB - numA; // Descending order (latest first)
            }
        }
        return 0;
    });
    
    return sortedVersions[0];
};

// Helper function to get sorted releases for navigation
const getSortedReleases = (releases: ReleaseNotesData): Release[] => {
    const versions = Object.keys(releases);
    
    const sortedVersions = versions.sort((a, b) => {
        const parseVersion = (version: string) => {
            return version.split('.').map(num => parseInt(num, 10));
        };
        
        const versionA = parseVersion(a);
        const versionB = parseVersion(b);
        
        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const numA = versionA[i] || 0;
            const numB = versionB[i] || 0;
            
            if (numA !== numB) {
                return numB - numA; // Descending order (latest first)
            }
        }
        return 0;
    });
    
    return sortedVersions.map(version => releases[version]);
};

// Inline styles (keeping existing styles)
const styles = {
    container: {
        maxWidth: '1140px',
        margin: '70px auto 0px',
        padding: '24px',
        backgroundColor: '#f9fafb',
        minHeight: '100vh'
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    flexBetween: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
    },
    flexCenter: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    title: {
        fontSize: '30px',
        fontWeight: 'bold',
        color: '#111827',
        margin: 0
    },
    subtitle: {
        fontSize: '20px',
        color: '#374151',
        marginBottom: '12px'
    },
    badge: {
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid'
    },
    majorBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        borderColor: '#c3ddfd'
    },
    minorBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        borderColor: '#bbf7d0'
    },
    patchBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        borderColor: '#fde68a'
    },
    dateText: {
        color: '#6b7280',
        fontSize: '14px'
    },
    summary: {
        color: '#4b5563',
        lineHeight: '1.6'
    },
    sectionButton: {
        width: '100%',
        padding: '24px',
        textAlign: 'left' as const,
        border: '1px solid',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    blueSection: {
        backgroundColor: '#eff6ff',
        borderColor: '#c3ddfd',
        color: '#1d4ed8'
    },
    greenSection: {
        backgroundColor: '#f0fdf4',
        borderColor: '#bbf7d0',
        color: '#166534'
    },
    purpleSection: {
        backgroundColor: '#faf5ff',
        borderColor: '#d8b4fe',
        color: '#7c3aed'
    },
    graySection: {
        backgroundColor: '#f9fafb',
        borderColor: '#e5e7eb',
        color: '#374151'
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '600',
        margin: 0
    },
    featureTitle: {
        fontWeight: '500',
        color: '#111827',
        marginBottom: '12px'
    },
    featureList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    featureItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '8px'
    },
    featureText: {
        color: '#374151',
        fontSize: '14px',
        lineHeight: '1.6'
    },
    impactCard: {
        background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
        borderRadius: '8px',
        border: '1px solid #c3ddfd',
        padding: '24px',
        marginBottom: '24px'
    },
    impactTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    impactItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '12px'
    },
    impactLabel: {
        fontWeight: '500',
        color: '#111827'
    },
    impactText: {
        color: '#374151',
        marginLeft: '4px'
    },
    navCard: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        padding: '16px',
        marginBottom: '24px'
    },
    navTitle: {
        fontWeight: '500',
        color: '#111827',
        marginBottom: '12px'
    },
    navButtons: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '8px'
    },
    navButton: {
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid',
        cursor: 'pointer',
        transition: 'colors 0.2s'
    },
    activeNavButton: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        borderColor: '#c3ddfd'
    },
    inactiveNavButton: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        borderColor: '#e5e7eb'
    },
    latestBadge: {
        backgroundColor: '#fbbf24',
        color: '#92400e',
        borderColor: '#fcd34d',
        fontSize: '11px',
        marginLeft: '4px',
        padding: '2px 6px'
    },
    controlButtons: {
        display: 'flex',
        gap: '8px'
    },
    controlButton: {
        padding: '4px 12px',
        fontSize: '14px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    expandButton: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    collapseButton: {
        backgroundColor: '#f3f4f6',
        color: '#374151'
    },
    footer: {
        textAlign: 'center' as const,
        paddingTop: '24px',
        borderTop: '1px solid #e5e7eb'
    },
    footerText: {
        color: '#4b5563',
        fontStyle: 'italic' as const
    }
};

const ReleaseHeader: React.FC<ReleaseHeaderProps> = ({ release }) => {
    const getTypeBadgeStyle = (type: 'major' | 'minor' | 'patch') => {
        switch (type) {
            case 'major': return { ...styles.badge, ...styles.majorBadge };
            case 'minor': return { ...styles.badge, ...styles.minorBadge };
            case 'patch': return { ...styles.badge, ...styles.patchBadge };
            default: return { ...styles.badge, ...styles.majorBadge };
        }
    };

    // Fix: Parse date correctly to avoid timezone issues
    const formatDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString();
    };

    return (
        <div style={styles.card}>
            <div style={styles.flexBetween}>
                <div style={styles.flexCenter}>
                    <h1 style={styles.title}>
                        BidBro Release {release.version}
                    </h1>
                    <span style={getTypeBadgeStyle(release.type)}>
                        {release.type.charAt(0).toUpperCase() + release.type.slice(1)} Release
                    </span>
                </div>
                <div style={styles.flexCenter}>
                    <Calendar style={{ width: '16px', height: '16px' }} />
                    <span style={styles.dateText}>{formatDate(release.date)}</span>
                </div>
            </div>
            <h2 style={styles.subtitle}>{release.title}</h2>
            <p style={styles.summary}>{release.summary}</p>
        </div>
    );
};

const FeatureSection: React.FC<FeatureSectionProps> = ({ section, isExpanded, onToggle }) => {
    const IconComponent = section.icon;

    const getSectionStyle = (color: 'blue' | 'green' | 'purple' | 'gray') => {
        switch (color) {
            case 'blue': return { ...styles.sectionButton, ...styles.blueSection };
            case 'green': return { ...styles.sectionButton, ...styles.greenSection };
            case 'purple': return { ...styles.sectionButton, ...styles.purpleSection };
            default: return { ...styles.sectionButton, ...styles.graySection };
        }
    };

    const getIconColor = (color: 'blue' | 'green' | 'purple' | 'gray') => {
        switch (color) {
            case 'blue': return '#2563eb';
            case 'green': return '#16a34a';
            case 'purple': return '#9333ea';
            default: return '#4b5563';
        }
    };

    return (
        <div style={{ ...styles.card, padding: 0, marginBottom: '16px' }}>
            <button
                onClick={onToggle}
                style={getSectionStyle(section.color)}
            >
                <div style={styles.flexCenter}>
                    <IconComponent style={{ width: '24px', height: '24px', color: getIconColor(section.color) }} />
                    <h3 style={styles.sectionTitle}>{section.title}</h3>
                </div>
                {isExpanded ? (
                    <ChevronUp style={{ width: '20px', height: '20px' }} />
                ) : (
                    <ChevronDown style={{ width: '20px', height: '20px' }} />
                )}
            </button>

            {isExpanded && (
                <div style={{ padding: '0 24px 24px' }}>
                    {section.features.map((feature: ReleaseFeature, featureIndex: number) => (
                        <div key={featureIndex} style={{ marginTop: '16px' }}>
                            <h4 style={styles.featureTitle}>{feature.title}</h4>
                            <ul style={styles.featureList}>
                                {feature.items.map((item: string, itemIndex: number) => (
                                    <li key={itemIndex} style={styles.featureItem}>
                                        <CheckCircle style={{ 
                                            width: '16px', 
                                            height: '16px', 
                                            color: '#10b981', 
                                            marginTop: '2px',
                                            flexShrink: 0
                                        }} />
                                        <span style={styles.featureText}>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ImpactSummary: React.FC<ImpactSummaryProps> = ({ impact }) => (
    <div style={styles.impactCard}>
        <h3 style={styles.impactTitle}>
            <Zap style={{ width: '20px', height: '20px', color: '#2563eb' }} />
            What This Means for You
        </h3>
        <div>
            <div style={styles.impactItem}>
                <Users style={{ width: '24px', height: '24px', color: '#2563eb', marginTop: '2px' }} />
                <div>
                    <span style={styles.impactLabel}>Customers:</span>
                    <span style={styles.impactText}>{impact.customers}</span>
                </div>
            </div>
            <div style={styles.impactItem}>
                <Hammer style={{ width: '24px', height: '24px', color: '#2563eb', marginTop: '2px' }} />
                <div>
                    <span style={styles.impactLabel}>Contractors:</span>
                    <span style={styles.impactText}>{impact.contractors}</span>
                </div>
            </div>
            <div style={styles.impactItem}>
                <Target style={{ width: '24px', height: '24px', color: '#2563eb', marginTop: '2px' }} />
                <div>
                    <span style={styles.impactLabel}>Everyone:</span>
                    <span style={styles.impactText}>{impact.overall}</span>
                </div>
            </div>
        </div>
    </div>
);

const ReleaseNavigation: React.FC<ReleaseNavigationProps> = ({ releases, currentVersion, onVersionChange }) => {
    const sortedReleases = getSortedReleases(releases);
    const latestVersion = getLatestReleaseVersion(releases);
    
    return (
        <div style={styles.navCard}>
            <h4 style={styles.navTitle}>All Releases</h4>
            <div style={styles.navButtons}>
                {sortedReleases.map((release: Release) => (
                    <button
                        key={release.version}
                        onClick={() => onVersionChange(release.version)}
                        style={currentVersion === release.version ? 
                            { ...styles.navButton, ...styles.activeNavButton } : 
                            { ...styles.navButton, ...styles.inactiveNavButton }
                        }
                    >
                        v{release.version}
                        {release.version === latestVersion && (
                            <span style={styles.latestBadge}>LATEST</span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Main component
const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ version }) => {
    // Get the latest version if no version is provided
    const latestVersion = getLatestReleaseVersion(releaseNotesData);
    const defaultVersion = version || latestVersion;
    
    const [currentVersion, setCurrentVersion] = useState<string>(defaultVersion);
    const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});

    const currentRelease = releaseNotesData[currentVersion];

    if (!currentRelease) {
        return (
            <React.Fragment>
                <Header />
                <div style={styles.container}>
                    <div style={{ 
                        backgroundColor: '#fef2f2', 
                        border: '1px solid #fecaca', 
                        borderRadius: '8px', 
                        padding: '16px' ,
                        marginTop: '15px'
                    }}>
                        <h3 style={{ color: '#991b1b', fontWeight: '500' }}>Release Not Found</h3>
                        <p style={{ color: '#b91c1c' }}>Version {currentVersion} not found.</p>
                        <button 
                            onClick={() => setCurrentVersion(latestVersion)}
                            style={{ ...styles.navButton, ...styles.activeNavButton }}
                        >
                            View Latest Release (v{latestVersion})
                        </button>
                    </div>
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    const toggleSection = (sectionId: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const expandAll = () => {
        const allExpanded: {[key: string]: boolean} = {};
        currentRelease.sections.forEach((section: ReleaseSection) => {
            allExpanded[section.id] = true;
        });
        setExpandedSections(allExpanded);
    };

    const collapseAll = () => {
        setExpandedSections({});
    };

    return (
        <React.Fragment>
            <Header />
            <div style={styles.container}>
                <ReleaseNavigation
                    releases={releaseNotesData}
                    currentVersion={currentVersion}
                    onVersionChange={setCurrentVersion}
                />

                <ReleaseHeader release={currentRelease} />

                <div style={styles.flexBetween}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>Release Details</h3>
                    <div style={styles.controlButtons}>
                        <button
                            onClick={expandAll}
                            style={{ ...styles.controlButton, ...styles.expandButton }}
                        >
                            Expand All
                        </button>
                        <button
                            onClick={collapseAll}
                            style={{ ...styles.controlButton, ...styles.collapseButton }}
                        >
                            Collapse All
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    {currentRelease.sections.map((section: ReleaseSection) => (
                        <FeatureSection
                            key={section.id}
                            section={section}
                            isExpanded={expandedSections[section.id]}
                            onToggle={() => toggleSection(section.id)}
                        />
                    ))}
                </div>

                <ImpactSummary impact={currentRelease.impact} />

                <div style={styles.footer}>
                    <p style={styles.footerText}>
                        This release focuses on elevating the professional appearance and user experience of BidBro
                        while maintaining the powerful matching and bidding capabilities you rely on.
                    </p>
                </div>
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default ReleaseNotes;